import { t } from 'i18next';

import { useGetCronJobDefinitions } from 'queries';

/**
 * The Quartz group an installation-wide job is scheduled under. Mirrors
 * `QuartzJobKeys.ALL_TENANTS_GROUP` on the backend.
 */
export const ALL_TENANTS_GROUP = '*';

/**
 * "(Alle tenants)" states something about the job, so it is composed here instead of being baked
 * into the job's own translation. Two of the BPM cleanup labels used to carry the words, which
 * left every other installation-wide job silently claiming to be a tenant's own schedule.
 */
export const withAllTenantsSuffix = (label: string, isAllTenants: boolean) =>
  isAllTenants ? `${label} ${t('cronJobs.allTenantsSuffix')}` : label;

export const cronJobLabel = (jobName: string) => t(`cronJobs.jobs.${jobName}`);

/**
 * Label a job that has already been scheduled. Its group says whether it covers every tenant, so
 * nothing needs to be looked up.
 */
export const scheduledCronJobLabel = (jobName: string, group: string) =>
  withAllTenantsSuffix(cronJobLabel(jobName), group === ALL_TENANTS_GROUP);

/**
 * Label a job type that has not been scheduled yet, where there is no group to read the scope off.
 * The scope comes from the API, which serves it from the same `QuartzJobKeys` the scheduler keys
 * jobs with, so a dropdown cannot promise something different from what it creates.
 *
 * The scope is not decoration: `bpm-history-cleanup` and `bpm-history-all-tenants-cleanup` share a
 * translation, and the suffix is the only thing that tells one tenant's cleanup apart from one
 * that runs across every tenant in the installation. So the caller is told whether the scope is
 * known yet - see `isScopeKnown` - and must not offer the choice until it is.
 */
export const useCronJobLabel = () => {
  const { data: definitions, isLoading, isError } = useGetCronJobDefinitions();

  const allTenantsJobNames = (definitions ?? [])
    .filter((definition) => definition.allTenants)
    .map((definition) => definition.jobName);

  return {
    /** False while the definitions are in flight, and false for good once they have failed. */
    isScopeKnown: !isLoading && !isError,
    /** The scopes could not be fetched at all, as opposed to not having arrived yet. */
    isScopeUnavailable: isError,
    cronJobLabel: (jobName: string) =>
      withAllTenantsSuffix(
        cronJobLabel(jobName),
        allTenantsJobNames.includes(jobName)
      )
  };
};
